import type {
  OpenFloatingFrameResult,
  ServerMessage,
} from 'ts-components/PiChat/protocol';
import {
  RESULT_DEADLINE_MARGIN_MS,
  resolveResultUpload,
  type ResolvedResultUpload,
} from 'ts-components/PiChat/presignedPut';
import type { PiChatFrameMeta } from 'ts-components/PiChat/usePiChat';
import { NOT_RENDERED_GRACE_MS } from 'ts-components/PiChat/capture/captureElement';
import { findCaptureTarget, isRendered } from '../capture/captureTarget';
import {
  floatingFrame,
  floatingFrameId,
} from 'ts-components/Plugins/floatingFrameRegistry';

export type OpenFloatingFrameMessage = Extract<
  ServerMessage,
  { type: 'ui.open_floating_frame' }
>;

export type FloatingFrameView = 'missing' | 'pending' | 'hidden' | 'rendered';

const LABEL = 'ui.open_floating_frame';
const OK: OpenFloatingFrameResult = { ok: true };
const NOT_FOUND: OpenFloatingFrameResult = { ok: false, code: 'not_found' };
const HIDDEN: OpenFloatingFrameResult = { ok: false, code: 'hidden' };
const UNAVAILABLE: OpenFloatingFrameResult = {
  ok: false,
  code: 'unavailable',
};

export const floatingFrameView = (
  appApiName: string,
  frameApiName: string
): FloatingFrameView => {
  const found = findCaptureTarget(
    document,
    { kind: 'floating_frame', frameApiName },
    appApiName
  );
  if (!found || found === 'unsupported') return 'missing';
  if (found.pending) return 'pending';
  return isRendered(found.element) ? 'rendered' : 'hidden';
};

export interface OpenFloatingFrameDeps {
  appApiName(): string | undefined;
  listed(id: string): boolean;
  view(appApiName: string, frameApiName: string): FloatingFrameView;
  sendResult(
    upload: ResolvedResultUpload<OpenFloatingFrameResult>,
    result: OpenFloatingFrameResult
  ): unknown;
  nextPaint(): Promise<void>;
  now(): number;
}

export interface KiCodeOpenFloatingFrame {
  handleServerMessage(message: ServerMessage, meta: PiChatFrameMeta): void;
  dispose(): void;
}

export const createKiCodeOpenFloatingFrame = (
  deps: OpenFloatingFrameDeps
): KiCodeOpenFloatingFrame => {
  let disposed = false;

  const open = async (
    appApiName: string,
    frameApiName: string,
    deadline: number
  ): Promise<OpenFloatingFrameResult> => {
    const id = floatingFrameId(appApiName, frameApiName);
    const frame = floatingFrame(id);
    if (!frame) return deps.listed(id) ? UNAVAILABLE : NOT_FOUND;
    if (frame.minimized) frame.setMinimized(false);
    let settleAt = deps.now() + NOT_RENDERED_GRACE_MS;
    while (!disposed && deps.now() < deadline) {
      const view = deps.view(appApiName, frameApiName);
      if (view === 'rendered') return OK;
      if (view === 'pending') {
        settleAt = deps.now() + NOT_RENDERED_GRACE_MS;
      } else if (deps.now() >= settleAt) {
        return view === 'hidden' ? HIDDEN : UNAVAILABLE;
      }
      await deps.nextPaint();
    }
    return UNAVAILABLE;
  };

  const handle = async (
    { frameApiName, upload: resultUpload }: OpenFloatingFrameMessage,
    meta: PiChatFrameMeta
  ) => {
    const upload = resolveResultUpload(
      resultUpload,
      meta.url,
      LABEL,
      UNAVAILABLE
    );
    if (!upload) return;
    const deadline = upload.expiresAt - RESULT_DEADLINE_MARGIN_MS;
    const appApiName = deps.appApiName();
    deps.sendResult(
      upload,
      disposed ||
        !meta.isCurrent() ||
        appApiName === undefined ||
        deps.now() >= deadline
        ? UNAVAILABLE
        : await open(appApiName, frameApiName, deadline)
    );
  };

  return {
    handleServerMessage(message, meta) {
      if (message.type === 'ui.open_floating_frame') void handle(message, meta);
    },
    dispose() {
      disposed = true;
    },
  };
};
